/** Element tagging — finds DOM elements using fingerprints and tags them with data attributes. */

import { state, debugLog } from "./state.js";
import type { TrackingRule } from "./types.js";

const DATA_ATTR_RULE = "data-tst-rule";
const DATA_ATTR_RULE_EVENT = "data-tst-rule-event";
const DATA_ATTR_RULE_VALUE = "data-tst-rule-value";
const DATA_ATTR_RULE_CURRENCY = "data-tst-rule-currency";

export { DATA_ATTR_RULE, DATA_ATTR_RULE_EVENT, DATA_ATTR_RULE_VALUE, DATA_ATTR_RULE_CURRENCY };

/** Secondary fingerprint checks to disambiguate multiple CSS matches. */
function matchesFingerprint(el: Element, rule: TrackingRule): boolean {
  if (rule.selectorTagName && el.tagName.toLowerCase() !== rule.selectorTagName) return false;
  if (rule.selectorHref && (el as HTMLAnchorElement).href !== rule.selectorHref) return false;
  return true;
}

/**
 * How many elements one rule may tag.
 *
 * A selector like `a[href^="tel:"]` is the whole point of typing one by hand,
 * and a page can legitimately hold a dozen of them. A selector like `a` is a
 * mistake, and tagging every link on the page would turn every click into a
 * conversion. The cap is the guard, and hitting it is logged rather than
 * absorbed: a rule that measures only part of the page must say so.
 */
const MAX_TAGGED_PER_RULE = 50;

/**
 * Find every element this rule applies to.
 *
 * Used to return a single element, and that was a silent loss. A rule pointing
 * at `.site-body a[href^="tel:"]` on a page with three phone numbers measured
 * one of them; clicks on the other two produced nothing, and nothing anywhere
 * said a thing. Observed on 6 August 2026 while rebuilding a prospect's site,
 * where a phone number appears in the header, the body and the footer.
 *
 * The fingerprint still narrows when it can. A rule made with the visual picker
 * carries the href of the exact element that was clicked, so it keeps meaning
 * that one element even when the generated selector is broader.
 */
function findElements(rule: TrackingRule): Element[] {
  // Strategy 1: CSS selector (most precise)
  if (rule.cssSelector) {
    try {
      const candidates = Array.prototype.slice.call(
        document.querySelectorAll(rule.cssSelector),
      ) as Element[];
      if (candidates.length === 1) return candidates;
      if (candidates.length > 1) {
        const narrowed = candidates.filter((el) => matchesFingerprint(el, rule));
        if (narrowed.length > 0) return narrowed;

        // Several matches and nothing to tell them apart.
        //
        // Tagging them all is right when the rule MEANS all of them, which is
        // what a typed selector like a[href^="tel:"] says, and those rules
        // carry an href fingerprint from the picker or none at all because
        // they were typed. It is wrong when the picker generated a selector
        // that happened to be unique on the page it was made on: `.btn-cta`
        // picked on the homepage would then fire on all twelve buttons of a
        // landing page, and each click would report a conversion.
        //
        // The two are told apart by whether the rule carries a fingerprint at
        // all. The picker always records the tag name and, for a link, the
        // href; a selector typed into the wizard carries neither. So a rule
        // with a fingerprint that now matches nothing is a picker rule that has
        // drifted, and it keeps its old behaviour of one element. A rule with
        // no fingerprint was typed, and typing a[href^="tel:"] means every
        // phone number on the page.
        const fromPicker = !!(rule.selectorHref || rule.selectorTagName);
        return fromPicker ? candidates.slice(0, 1) : candidates;
      }
    } catch {
      // invalid selector — fall through to text matching
    }
  }

  // Strategy 2: elementText fallback (walk interactive elements)
  if (rule.elementText) {
    const interactives = document.querySelectorAll(
      'a, button, [role="button"], input[type="submit"], [tabindex]',
    );
    const target = rule.elementText.toLowerCase();
    const matches: Element[] = [];
    for (const el of interactives) {
      const text = ((el as HTMLElement).innerText || el.textContent || "").trim().toLowerCase();
      if (text.includes(target) && matchesFingerprint(el, rule)) matches.push(el);
    }
    return matches;
  }

  return [];
}

/** Tag a single element with rule data attributes. */
function tagElement(el: Element, rule: TrackingRule): void {
  el.setAttribute(DATA_ATTR_RULE, rule.id);
  el.setAttribute(DATA_ATTR_RULE_EVENT, rule.eventName);
  if (rule.eventValue != null) {
    el.setAttribute(DATA_ATTR_RULE_VALUE, String(rule.eventValue));
  }
  if (rule.currency) {
    el.setAttribute(DATA_ATTR_RULE_CURRENCY, rule.currency);
  }
  debugLog("tagElement", "tagged", rule.id, el.tagName, rule.cssSelector);
}

/** Tag all click rules on the current page. */
export function tagClickRules(): void {
  if (!state.config || !state.config.rules) return;

  for (const rule of state.config.rules) {
    if (rule.triggerType !== "click") continue;
    const elements = findElements(rule);
    if (elements.length === 0) {
      debugLog("tagClickRules", "element not found for rule", rule.id, rule.cssSelector, rule.elementText);
      continue;
    }

    if (elements.length > MAX_TAGGED_PER_RULE) {
      debugLog(
        "tagClickRules",
        "rule " + rule.id + " matches " + elements.length + " elements; tagging the first "
          + MAX_TAGGED_PER_RULE + " only. The selector is almost certainly too broad: "
          + rule.cssSelector,
      );
    }

    elements.slice(0, MAX_TAGGED_PER_RULE).forEach((el) => tagElement(el, rule));
    debugLog("tagClickRules", "tagged " + Math.min(elements.length, MAX_TAGGED_PER_RULE) + " element(s) for rule", rule.id);
  }
}

/** Re-tag after SPA navigation or DOM changes. */
export function retagClickRules(): void {
  // Remove existing tags first
  document.querySelectorAll(`[${DATA_ATTR_RULE}]`).forEach((el) => {
    el.removeAttribute(DATA_ATTR_RULE);
    el.removeAttribute(DATA_ATTR_RULE_EVENT);
    el.removeAttribute(DATA_ATTR_RULE_VALUE);
    el.removeAttribute(DATA_ATTR_RULE_CURRENCY);
  });
  tagClickRules();
}
